import React, { useRef } from 'react';
import Icon from '../shared/Icon';
import TooltipLayer from '../shared/Tooltip';
import AssessmentWaveform from './AssessmentWaveform';
import { AUDIO_SRC, TRANSCRIPT, clock, transcriptParagraphs } from '../../../models/assessmentEvaluation';

/* The play control and the wave, then the file name and the clock. Nothing
   else: no skip, no slider, no mute, so the wave is the scrubber. A recording
   that can't load leaves Play disabled but focusable, so its tooltip can say
   why (Design, 2026-11-04). */
function Player({ fileName, player }) {
  const scopeRef = useRef(null);
  return (
    <div ref={scopeRef} className="assessment-audio-block" data-component="Assessment Audio">
      <audio ref={player.audioRef} preload="metadata" src={AUDIO_SRC || undefined} />
      <h4 className="assessment-eval-head">Audio</h4>
      <div className="assessment-player-row">
        <button
          type="button"
          data-component="Icon Button - Small - Primary"
          className="assessment-play-btn"
          aria-label={player.playing ? 'Pause' : 'Play'}
          aria-disabled={player.failed || undefined}
          data-tip={player.failed ? "Can't play this recording" : undefined}
          data-tip-note={player.failed ? "The file didn't load" : undefined}
          onClick={player.toggle}
        >
          <Icon name={player.playing ? 'pause' : 'play'} size={20} />
        </button>
        <AssessmentWaveform
          time={player.time}
          duration={player.duration}
          onSeek={player.seek}
          onToggle={player.toggle}
        />
      </div>
      <div className="assessment-audio-meta">
        <span className="assessment-audio-file" title={fileName}>{fileName}</span>
        <span className="assessment-audio-time">{clock(player.time)} / {clock(player.duration)}</span>
      </div>
      <TooltipLayer scopeRef={scopeRef} />
    </div>
  );
}

/* Five bars on a 20 box, each growing about its own middle on a stagger. The
   only animated mark in the build; it holds still for reduced motion. */
function Waiting({ fromTranscript }) {
  return (
    <div className="assessment-transcript-waiting">
      <span className="assessment-wave-anim" aria-hidden="true"><span /><span /><span /><span /><span /></span>
      <span>{fromTranscript ? 'Reading transcript…' : 'Transcribing audio…'}</span>
    </div>
  );
}

/* Built on a recording: timestamped rows, each a button that seeks. The row
   the playhead is in carries the wash and nothing else, so the line never
   reflows as the clip runs. */
function TranscriptRows({ activeIndex, onSeek }) {
  return TRANSCRIPT.map((seg, i) => (
    <button
      key={seg.at}
      type="button"
      data-component="Transcript Row"
      data-segment={i}
      className={`assessment-tx-seg${i === activeIndex ? ' is-active' : ''}`}
      onClick={() => onSeek(seg.at)}
    >
      <span className="assessment-tx-time">{clock(seg.at)}</span>
      <span className="assessment-tx-text">{seg.text}</span>
    </button>
  ));
}

/* Built on a transcript: no times, so a body of text, three segments to a
   paragraph. Every passage a card can cite is wrapped, and only the one asked
   for is marked, with the Evidence Marker. */
function TranscriptBody({ markedIndex }) {
  return (
    <div className="assessment-tx-body">
      {transcriptParagraphs().map((para) => (
        <p key={para[0].index}>
          {para.map((p, i) => (
            <React.Fragment key={p.index}>
              {i > 0 && ' '}
              <span
                data-segment={p.index}
                tabIndex={-1}
                className={`assessment-tx-ref${p.index === markedIndex ? ' is-active' : ''}`}
              >
                {p.text}
              </span>
            </React.Fragment>
          ))}
        </p>
      ))}
    </div>
  );
}

/**
 * The right column (Assessment Audio, 1208:12357): the player, then the
 * transcript, in one card. Built on a transcript there is no player and no
 * time anywhere, and the file name moves under the Transcript title.
 * `transcriptRef` is the transcript's own scroller.
 */
function AssessmentAudioCard({ fileName, fromTranscript, processing, player, activeIndex, markedIndex, transcriptRef, onSeek }) {
  const fileLine = fromTranscript && (
    <div className={`assessment-audio-meta${processing ? '' : ' assessment-audio-pad'}`}>
      <span className="assessment-audio-file" title={fileName}>{fileName}</span>
    </div>
  );
  return (
    <div className={`assessment-audio-card${processing ? ' assessment-audio-card--waiting' : ''}`}>
      {!fromTranscript && <Player fileName={fileName} player={player} />}
      <div data-component="Transcript Block" className="assessment-transcript-block">
        <h4 className={`assessment-eval-head${processing ? '' : ' assessment-audio-pad'}`}>Transcript</h4>
        {fileLine}
        {processing ? <Waiting fromTranscript={fromTranscript} /> : (
          <div ref={transcriptRef} className="assessment-transcript" role="region" aria-label="Transcript">
            {fromTranscript
              ? <TranscriptBody markedIndex={markedIndex} />
              : <TranscriptRows activeIndex={activeIndex} onSeek={onSeek} />}
          </div>
        )}
      </div>
    </div>
  );
}

export default AssessmentAudioCard;
